import React, { useState, useEffect } from 'react'

import {
  getOptimizelyFallbackId,
  getScreenSize,
  getLocale,
  isIPad,
  getCookie,
  usePrevious,
  isTouchDevice as isTouchDeviceUtil,
  getMediaTouchpoint,
} from '@pb/utils'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import { detect } from 'detect-browser'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { CRMEN_ANSWERED_DONATE_VOUCHER } from 'features/account/donate-voucher/constants'
import purchasesSelectors from 'features/account/purchases/duck/selectors'
import checkoutSelectors, {
  getHasCompletePurchaseClicked,
  getIsOrderOrchestration,
  getIsPaypalSelected,
  getPreSelectedBankId,
  getHasNordicHomeOrIBDelivery,
  getUserEditedAddressFromSuggestions,
  getHasOpenedDeliverySection,
  getDeliveryCost,
} from 'features/checkout/duck/selectors'
import { mapItemsInOrder } from 'features/checkout/helpers/experimentHelpers'

import optimizelyInstance from './optimizely'

const optimizelyUserId = getOptimizelyFallbackId()
const locale = getLocale()
const browser = detect()
const screenSize = getScreenSize()
const isTouchDevice = isTouchDeviceUtil()
const firstSession = window.abTest?.visitor.firstSession
const crmenAnsweredDonateVoucher =
  JSON.parse(localStorage.getItem(CRMEN_ANSWERED_DONATE_VOUCHER) as string) || false
const onPageLoadUrl = window.location.toString().toLowerCase()

const welcomeOffer = getCookie('aamgasegs') ?? ''
const fnCampaignEnabled = getCookie('aemsegs') ?? ''
const WelcomeOfferABCookie = getCookie('WelcomeOfferABCookie') ?? ''
const optOut = getCookie('ab_optout') ?? ''
const aamSegments = getCookie('aamoptsegs') ?? ''
const qaMode = window.abTest?.store?.get('qaMode') ?? false

const HmOptimizelyProvider: React.FC = (props) => {
  const userHasOrderRecommendations = useSelector(purchasesSelectors.getUserHasOrderRecommendations)
  const customerType = useSelector(appSelectors.getCustomerType) || 'NA'
  const [customerTypeEntry, setCustomerTypeEntry] = useState('NA')
  const [hasNordicHomeDeliveryOnEntry, setHasNordicHomeDeliveryOnEntry] = useState(false)
  const previousCustomerTypeEntry = usePrevious(customerType)

  const hasOpenedDeliverySection = useSelector(getHasOpenedDeliverySection)
  const deliveryCost = useSelector(getDeliveryCost)?.value
  const subTotal = useSelector(checkoutSelectors.getSubTotal)
  const cartItemsQuantity = useSelector(checkoutSelectors.getCartItemsQuantity)
  const itemsInOrder = mapItemsInOrder(cartItemsQuantity)
  const numOfParcels = useSelector(checkoutSelectors.getParcels).length
  const multipleParcels = numOfParcels ? numOfParcels > 1 : undefined
  const [multipleParcelsEntry, setMultipleParcelsEntry] = useState(multipleParcels)
  const previousMultipleParcelsEntry = usePrevious(multipleParcelsEntry)
  const hasCompletePurchaseClicked = useSelector(getHasCompletePurchaseClicked)
  // Used in experiment - remove on cleanup [COWEB-2946]
  const userEditedAddressFromSuggestions = useSelector(getUserEditedAddressFromSuggestions)
  const shouldShowProductsDetails = useSelector(checkoutSelectors.getShouldShowProductsDetails)
  const [thumbnailModalHasBeenOpened, setThumbnailModalHasBeenOpened] =
    useState(shouldShowProductsDetails)

  const isOrderOrchestration = useSelector(getIsOrderOrchestration)
  const isCrossBrands = useSelector(checkoutSelectors.getIsCrossBrandsBasket)
  const hasSelectedBankId = !!useSelector(checkoutSelectors.getSelectedBankId)
  const isBankIDPreSelected = useSelector(getPreSelectedBankId) !== undefined
  const isPaypalSelected = useSelector(getIsPaypalSelected)
  // Used in experiment - remove on cleanup [COWEB-2943]
  const hasNordicHomeOrIBDelivery = useSelector(getHasNordicHomeOrIBDelivery)

  // Make OFS instance available globally elsewhere in the journey
  useEffect(() => {
    ;(window as any).optimizelyFullStackInstance = optimizelyInstance
  }, [])

  // handling of customer type on checkout entry
  useEffect(() => {
    if (
      customerType !== 'NA' &&
      !(previousCustomerTypeEntry === 'TEMPORARY' && customerType === 'REGISTERED')
    ) {
      setCustomerTypeEntry(customerType)
    }
  }, [customerType])

  useEffect(() => {
    if (
      typeof previousMultipleParcelsEntry === 'undefined' &&
      typeof multipleParcels !== 'undefined'
    ) {
      setMultipleParcelsEntry(multipleParcels)
    }
  }, [previousMultipleParcelsEntry, multipleParcels])

  useEffect(() => {
    if (shouldShowProductsDetails) {
      setThumbnailModalHasBeenOpened(shouldShowProductsDetails)
    }
  }, [shouldShowProductsDetails])

  useEffect(() => {
    if (hasNordicHomeOrIBDelivery && !hasOpenedDeliverySection)
      setHasNordicHomeDeliveryOnEntry(true)
  }, [hasNordicHomeOrIBDelivery])

  const user = {
    id: optimizelyUserId,
    attributes: {
      locale, // duly support for Optimizely Audiences
      crmenAnsweredDonateVoucher,
      onPageLoadUrl,
      browserName: browser?.name,
      browserType: browser?.type,
      welcomeOffer,
      fnCampaignEnabled,
      WelcomeOfferABCookie,
      os: browser?.os,
      browserVersion: browser?.version,
      touchpoint: getMediaTouchpoint(),
      screenSize,
      isTouchDevice,
      isIPad: isIPad(),
      customerTypeEntry,
      customerType,
      ...(typeof firstSession !== 'undefined' && {
        userRecurrence: firstSession === true ? 'New' : 'Returning',
      }),
      visitedCheckout: false,
      visitedOrderDetailPage: false,
      userHasOrderRecommendations,
      hasClickedCarousel: false,
      optOut,
      isCrossBrands,
      ...(typeof deliveryCost !== 'undefined' && { deliveryCost }),
      ...(typeof subTotal !== 'undefined' && { subTotal }),
      ...(typeof itemsInOrder !== 'undefined' && { itemsInOrder }),
      ...(typeof multipleParcels !== 'undefined' && { multipleParcels }),
      ...(typeof multipleParcelsEntry !== 'undefined' && { multipleParcelsEntry }),
      thumbnailModalHasBeenOpened,
      hasSelectedBankId,
      aam_segments: aamSegments,
      qaMode,
      isBankIDPreSelected,
      hasCompletePurchaseClicked,
      isPaypalSelected,
      isOrderOrchestration,
      hasNordicHomeDeliveryOnEntry,
      userEditedAddressFromSuggestions,
    },
  }

  return (
    <OptimizelyProvider optimizely={optimizelyInstance} user={user}>
      {props.children}
    </OptimizelyProvider>
  )
}

export default HmOptimizelyProvider
